import { bookingsBookItClick } from '@wix/bi-logger-wixboost-ugc/v2';
import { getTrackingInfoForNavigateToServicePageClick } from '@wix/bookings-calendar-catalog-viewer-analytics-adapter';
import {
  navigateToBookingsCalendarPage,
  navigateToBookingsServicePage,
} from '@wix/bookings-catalog-calendar-viewer-navigation';
import { trackAnalytics } from '@wix/bookings-catalog-calendar-viewer-utils';
import { Basket } from '@wix/bookings-services-preferences-modal/types';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { ServiceListContext } from '../../context/createServiceListContext';
import { EnrichedService } from '../../types/types';
import {
  ActionNames,
  ReferralInfo,
  serviceListReferralInfo,
} from '../../utils/bi/consts';
import { getLocationId } from '../../utils/getLocationId/getLocationId';
import { getResourceQueryParam } from '../../utils/queryParams/queryParams';
import { WidgetViewModel } from '../../viewModel/viewModel';
import { OnDialogCloseAction } from '../onDialogClose/onDialogClose';
import { NavigationTarget } from './consts';

export type NavigateToNextPageAction = ({
  service,
  target,
  referralInfo,
  basket,
}: {
  service?: EnrichedService;
  target: NavigationTarget;
  referralInfo?: ReferralInfo;
  basket?: Basket;
}) => Promise<void>;

export const createNavigateToNextPageAction = ({
  flowAPI,
  serviceListContext,
  widgetViewModel,
  onDialogClose,
}: {
  flowAPI: ControllerFlowAPI;
  serviceListContext: ServiceListContext;
  widgetViewModel: WidgetViewModel;
  onDialogClose: OnDialogCloseAction;
}): NavigateToNextPageAction => {
  const {
    bi,
    controllerConfig: { wixCodeApi, platformAPIs },
  } = flowAPI;
  const { businessInfo } = serviceListContext;
  const closeDialogAfterNavigation = () => void setTimeout(() => onDialogClose({ doNotReturnFocus: true }), 1000);

  return async ({ basket, service, target, referralInfo }) => {
    const queryParam = getResourceQueryParam(wixCodeApi);
    const locationId = getLocationId(flowAPI, widgetViewModel);
    const optionalParams = {
      referral: serviceListReferralInfo,
      ...(queryParam.staff ? { staff: queryParam.staff } : {}),
      ...(queryParam.resource ? { resource: queryParam.resource } : {}),
      ...(locationId ? { location: locationId } : {}),
    };

    if (target === NavigationTarget.CALENDAR_PAGE) {
      flowAPI.panoramaClient.transaction('book-now').start();
    }

    if (!service) {
      if (!basket) {
        console.error('both service and basket are missing');
        return;
      }

      bi?.report(
        bookingsBookItClick({
          service_id: basket.selectedServicesPreferences
            .map(({ service }) => service.id)
            .join(','),
          type: basket.selectedServicesPreferences[0].service.type,
          isPendingApproval:
            basket.selectedServicesPreferences[0].service.onlineBooking
              ?.requireManualApproval!,
          referralInfo,
          actionName: ActionNames[target],
        }),
      );

      flowAPI.panoramaClient.transaction('book-now').finish();
      return navigateToBookingsCalendarPage(
        flowAPI.controllerConfig.wixCodeApi,
        flowAPI.controllerConfig.platformAPIs,
        {
          serviceSlugOrBasket: basket,
          navigationContext: optionalParams,
        },
      ).then(closeDialogAfterNavigation);
    }
    const data = {
      service,
      businessName: businessInfo.name,
    };

    if (target === NavigationTarget.SERVICE_PAGE) {
      const trackingInfo = getTrackingInfoForNavigateToServicePageClick(data);

      trackAnalytics({
        wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
        eventId: trackingInfo.eventId as any,
        payload: trackingInfo.payload,
      });
    }

    bi?.report(
      bookingsBookItClick({
        service_id: service.id!,
        type: service.type!,
        isPendingApproval: service.onlineBooking?.requireManualApproval!,
        referralInfo,
        actionName: ActionNames[target],
      }),
    );
    try {
      switch (target) {
        case NavigationTarget.CALENDAR_PAGE:
          flowAPI.panoramaClient.transaction('book-now').finish();
          return navigateToBookingsCalendarPage(wixCodeApi, platformAPIs, {
            serviceSlugOrBasket: service.mainSlug?.name!,
            navigationContext: optionalParams,
          }).then(closeDialogAfterNavigation);

        case NavigationTarget.SERVICE_PAGE:
          return navigateToBookingsServicePage(
            wixCodeApi,
            {
              serviceSlug: service.dummy ? '' : service.mainSlug?.name!,
              ...optionalParams,
            },
          ).then(closeDialogAfterNavigation);
      }
    } catch (e) {
      console.error(e);
    }
  };
};
